import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';
import { Heart, Globe, Leaf, TreePine, Droplets, Wind } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultSections = [
  {
    title: { fi: 'Empatia', fr: 'Empathie', en: 'Compassion' },
    content: {
      fi: 'Jokainen eläin tuntee kipua ja iloa. Veganismilla vähennämme kärsimystä. Vegaanisena emme osallistu eläinten hyväksikäyttöön. Jokainen elävä olento ansaitsee kunnioituksen ja oikeuden elää vapaana.',
      fr: "Chaque animal ressent la douleur et la joie. Le véganisme réduit la souffrance. En tant que végans, nous ne participons pas à l'exploitation animale. Chaque être vivant mérite le respect et le droit de vivre libre.",
      en: "Every animal feels pain and joy. Veganism reduces suffering. As vegans, we don't participate in animal exploitation. Every living being deserves respect and the right to live free."
    }
  },
  {
    title: { fi: 'Hiilijalanjälki', fr: 'Empreinte Carbone', en: 'Carbon Footprint' },
    content: {
      fi: 'Eläintuotanto aiheuttaa 14.5% maailman kasvihuonepäästöistä - enemmän kuin kaikki liikenne yhteensä. Kasvisruokavalio vähentää merkittävästi hiilijalanjälkeämme.',
      fr: "L'élevage cause 14.5% des émissions mondiales de gaz à effet de serre - plus que tous les transports réunis. Un régime végétal réduit considérablement notre empreinte carbone.",
      en: 'Animal agriculture causes 14.5% of global greenhouse gas emissions - more than all transportation combined. A plant-based diet significantly reduces our carbon footprint.'
    }
  },
  {
    title: { fi: 'Metsät', fr: 'Forêts', en: 'Forests' },
    content: {
      fi: '80% Amazonin metsäkadosta johtuu karjankasvatuksesta. Vegaaninen ruokavalio suojelee metsiä ja biodiversiteettiä.',
      fr: "80% de la déforestation amazonienne est due à l'élevage. Un régime végétalien protège les forêts et la biodiversité.",
      en: '80% of Amazon deforestation is due to cattle ranching. A vegan diet protects forests and biodiversity.'
    }
  },
  {
    title: { fi: 'Vesi', fr: 'Eau', en: 'Water' },
    content: {
      fi: 'Yhden naudan hampurilaisen tuottaminen vaatii 2500 litraa vettä. Kasvisruoka säästää vettä ja suojelee vesistöjä.',
      fr: "La production d'un hamburger de boeuf nécessite 2500 litres d'eau. Les aliments végétaux économisent l'eau et protègent les cours d'eau.",
      en: 'Producing one beef burger requires 2500 liters of water. Plant foods save water and protect waterways.'
    }
  }
];

export const WhyVegan = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getSEOContent = () => {
    const content = {
      en: {
        title: 'Why Go Vegan? - Animal Welfare & Environment | love4vegan',
        description: 'Discover why people choose veganism. Learn about animal welfare, environmental impact, and how your food choices can make a difference.'
      },
      fi: {
        title: 'Miksi Veganismi? - Eläinten Hyvinvointi ja Ympäristö | love4vegan',
        description: 'Miksi ihmiset valitsevat veganismin? Opi eläinten hyvinvoinnista, ympäristövaikutuksista ja ruokavalintojen merkityksestä.'
      },
      fr: {
        title: 'Pourquoi Végan? - Bien-être Animal & Environnement | love4vegan',
        description: 'Découvrez pourquoi les gens choisissent le véganisme. Protection animale, impact environnemental et alimentation responsable.'
      }
    };
    return content[language] || content.en;
  };

  const seoContent = getSEOContent();

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/why_vegan`);
      setPageData(response.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use DB content or defaults
  const title = pageData?.title || { fi: 'Miksi Veganismi?', fr: 'Pourquoi le Véganisme?', en: 'Why Veganism?' };
  const intro = pageData?.content || { 
    fi: 'Valitsemme veganismin eläinten hyvinvoinnin ja ekologisten syiden vuoksi.',
    fr: 'Nous choisissons le véganisme pour le bien-être animal et des raisons écologiques.',
    en: 'We choose veganism for animal welfare and ecological reasons.'
  };
  
  const sections = (pageData?.sections && pageData.sections.length > 0) ? pageData.sections : defaultSections;
  const icons = [Heart, Globe, TreePine, Droplets, Leaf, Wind];

  return (
    <div className="min-h-screen" data-testid="why-vegan-page">
      <SEO 
        title={seoContent.title}
        description={seoContent.description}
        image="https://images.unsplash.com/photo-1751200270667-cb13feeac24c?w=1200"
        url="https://love4vegan.com/why-vegan"
        type="article"
        keywords="why vegan, veganism, animal welfare, miksi vegaani, pourquoi végan"
      />
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1751200270667-cb13feeac24c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt="Fresh ingredients"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              {getLocalizedText(title)}
            </h1>
            <p className="text-xl text-foreground/70 leading-relaxed">
              {getLocalizedText(intro)}
            </p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {sections.map((section, index) => {
              const Icon = icons[index % icons.length];
              const isEven = index % 2 === 0;
              return (
                <div 
                  key={index}
                  className={`group p-8 rounded-3xl transition-all duration-300 hover:-translate-y-2 ${
                    isEven ? 'bg-card border border-border hover:shadow-xl' : 'bg-primary/5 hover:bg-primary/10'
                  }`}
                  data-testid={`why-vegan-section-${index}`}
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${
                    isEven ? 'bg-secondary/20' : 'bg-primary/20'
                  }`}>
                    <Icon className={`h-7 w-7 ${isEven ? 'text-secondary' : 'text-primary'}`} />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">
                    {getLocalizedText(section.title)}
                  </h3>
                  <p className="text-foreground/70 leading-relaxed">
                    {getLocalizedText(section.content)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="font-accent text-3xl mb-4">
            {getLocalizedText({
              fi: 'Jokainen valinta merkitsee',
              fr: 'Chaque choix compte',
              en: 'Every choice matters'
            })}
          </div>
          <p className="text-primary-foreground/80 leading-relaxed text-lg">
            {getLocalizedText({
              fi: 'Kun valitset vegaanisen aterian, valitset myötätunnon eläimiä kohtaan ja kestävämmän tulevaisuuden planeetallemme. Se on pieni teko, jolla on suuri vaikutus.',
              fr: "Quand vous choisissez un repas végétalien, vous choisissez la compassion envers les animaux et un avenir plus durable pour notre planète. C'est un petit geste avec un grand impact.",
              en: 'When you choose a vegan meal, you choose compassion for animals and a more sustainable future for our planet. It\'s a small act with a big impact.'
            })}
          </p>
        </div>
      </section>
    </div>
  );
};
